import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Container } from "@mui/material";
import { Footer } from "components/Footer";
import { StepInfo, Steps } from "common/Steps";
import WatchSwapper, { WatchSwapperRef } from "components/WatchSwapper";
import WatchSwapperConfirm, {
  WatchSwapperConfirmRef,
} from "components/WatchSwapperConfirm";
import StepsHeader from "components/StepsHeader";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { AppRoutes } from "core/AppRoutes";
import {
  FocusOn,
  setFocusTarget,
  setSuccessMessage,
} from "./StudyParticipantsStateSlice";
import SDPErrorSnackbar from "components/SDPErrorSnackbar";

enum WatchSwapSteps {
  Swap = 0,
  Confirm = 1,
}

// The dialog for updating a participant status
const ParticipantsWatchSwap: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appState = useAppSelector((state) => state.appState);

  // Redirect to home screen when page is refreshed.
  useEffect(() => {
    if (!appState.userConfigured) {
      navigate(AppRoutes.ROOT);
    }
  }, [appState.userConfigured, navigate]);

  const exitPage = useCallback(() => {
    dispatch(setFocusTarget(FocusOn.SwapWatches));
    navigate(AppRoutes.SITE_PARTICIPANTS);
  }, [navigate, dispatch]);

  const watchSwapperRef = useRef<WatchSwapperRef>(null);
  const watchSwapperConfirmRef = useRef<WatchSwapperConfirmRef>(null);

  // Store next button text in a state so that we can set it with
  // a delay to prevent the next button text is announced first when
  // navigate to a new screen.
  const [nextButtonText, setNextButtonText] = useState("Next");

  const [swaps, setSwaps] = useState<Map<string, string>>(new Map([["", ""]]));

  const [confirmLoading, setConfirmLoading] = useState(false);

  const [prevStepId, setPrevStepId] = useState<number>(-1);
  const [stepId, setStepId] = useState<number>(WatchSwapSteps.Swap);

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFirstTimeOnPage, setIsFirstTimeOnPage] = useState(true);

  const stepInfo: Array<StepInfo> = useMemo(() => {
    return [
      {
        id: WatchSwapSteps.Swap,
        page: (
          <WatchSwapper
            ref={watchSwapperRef}
            swaps={swaps}
            setSwaps={setSwaps}
          />
        ),
        onPrevious: () => {},
        isPreviousDisabled: true,
        nextButtonText: "Next",
        isNextButtonDisabled: false,
        onNext: () => {
          setIsFirstTimeOnPage(false);
          if (watchSwapperRef.current?.validateSwaps()) {
            setPrevStepId(WatchSwapSteps.Swap);
            setStepId(WatchSwapSteps.Confirm);
          }
        },
      },
      {
        id: WatchSwapSteps.Confirm,
        page: (
          <WatchSwapperConfirm
            ref={watchSwapperConfirmRef}
            swaps={swaps}
            onSuccess={() => {
              dispatch(setSuccessMessage("Watch assignments updated"));
              exitPage();
            }}
            onError={(msg: string) => {
              setErrorMessage(msg);
              setShowErrorSnackbar(true);
            }}
          />
        ),
        onPrevious: () => {
          setStepId(prevStepId);
          setPrevStepId(WatchSwapSteps.Swap);
        },
        isPreviousDisabled: false,
        nextButtonText: "Save",
        isNextButtonDisabled: false,
        isNextButtonLoading: confirmLoading,
        onNext: async () => {
          setConfirmLoading(true);
          await watchSwapperConfirmRef.current?.swapWatches();
          setConfirmLoading(false);
        },
      },
    ];
  }, [confirmLoading, prevStepId, swaps, dispatch, exitPage]);

  const steps: Steps = useMemo(() => {
    return new Steps(stepInfo, WatchSwapSteps.Swap);
  }, [stepInfo]);

  const resetFields = useCallback(() => {
    setPrevStepId(-1);
    setStepId(steps.default.id);
    setSwaps(new Map([["", ""]]));
    setNextButtonText("Next");
  }, [steps.default.id]);

  useEffect(() => {
    resetFields();
  }, [resetFields]);

  // focus on form title after transition to previous or next screen
  useEffect(() => {
    if (!isFirstTimeOnPage) {
      const timeoutId = setTimeout(() => {
        if (stepId === WatchSwapSteps.Swap) {
          watchSwapperRef.current?.focusOnTitle();
          setNextButtonText("Next");
        } else if (stepId === WatchSwapSteps.Confirm) {
          watchSwapperConfirmRef.current?.focusOnTitle();
          setNextButtonText("Save");
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isFirstTimeOnPage, stepId]);

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100vh",
        }}
      >
        <StepsHeader
          scrollTarget={containerRef.current ?? undefined}
          title="Swap watches"
          stepId={stepId}
          steps={steps}
          onClose={exitPage}
        />
        <Container
          role="main"
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "60%",
            height: "100%",
            alignItems: "center",
            padding: "48px 0px 48px",
            overflowY: "auto",
          }}
        >
          {steps.get(stepId)!.page}
          <Box sx={{ marginTop: "64px", flexGrow: 1 }}></Box>
          <Box sx={{ width: "670px" }}>
            <Footer
              leftButtonText="Previous"
              isLeftButtonDisabled={steps.get(stepId)!.isPreviousDisabled}
              handleClickLeftButton={steps.get(stepId)!.onPrevious}
              rightButtonText={nextButtonText}
              isRightButtonDisabled={steps.get(stepId)!.isNextButtonDisabled}
              isRightButtonLoading={steps.get(stepId)!.isNextButtonLoading}
              handleClickRightButton={steps.get(stepId)!.onNext}
            />
          </Box>
        </Container>
      </Box>
      <SDPErrorSnackbar
        showSnackbar={showErrorSnackbar}
        setShowSnackbar={setShowErrorSnackbar}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default ParticipantsWatchSwap;
